import { useState } from 'react';
import {
  Activity,
  Heart,
  TrendingDown,
  MessageCircle,
  Play,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Users,
  Shield,
  Clock,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Zap,
  X,
  Target,
  Menu,
  AlertTriangle,
  User
} from 'lucide-react';
import { InstagramReelsWidget } from './InstagramReelsWidget';

interface LandingPageProps {
  onSignIn: () => void;
  onViewPackages?: () => void;
}

export function LandingPage({ onSignIn, onViewPackages }: LandingPageProps) {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [chatMessages, setChatMessages] = useState<{text: string, isUser: boolean}[]>([
    { text: "Hello! I'm your DropSugar assistant. How can I help you today?", isUser: false }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isDiabetesModalOpen, setIsDiabetesModalOpen] = useState(false);
  const [isObesityModalOpen, setIsObesityModalOpen] = useState(false);
  const [isFattyLiverModalOpen, setIsFattyLiverModalOpen] = useState(false);
  const [isHypertensionModalOpen, setIsHypertensionModalOpen] = useState(false);
  const [isPCODModalOpen, setIsPCODModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isCalculatorsExpanded, setIsCalculatorsExpanded] = useState(false);
  const [activeCalculator, setActiveCalculator] = useState<'bmi' | 'homa' | 'egfr'>('bmi');

  // BMI Calculator states
  const [bmiHeight, setBmiHeight] = useState('');
  const [bmiWeight, setBmiWeight] = useState('');
  const [bmiResult, setBmiResult] = useState<number | null>(null);

  // HOMA-IR Calculator states
  const [fastingGlucose, setFastingGlucose] = useState('');
  const [fastingInsulin, setFastingInsulin] = useState('');
  const [homaResult, setHomaResult] = useState<number | null>(null);

  // eGFR Calculator states
  const [creatinine, setCreatinine] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [egfrResult, setEgfrResult] = useState<number | null>(null);

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    if (sectionId === 'calculators') setIsCalculatorsExpanded(true);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSendMessage = () => {
    if (chatInput.trim()) {
      setChatMessages([...chatMessages,
        { text: chatInput, isUser: true },
        { text: "Thank you for your message. Our AI assistant is analyzing your query...", isUser: false }
      ]);
      setChatInput('');
    }
  };

  const calculateBMI = () => {
    const heightM = parseFloat(bmiHeight) / 100;
    const weightKg = parseFloat(bmiWeight);
    if (heightM > 0 && weightKg > 0) {
      const bmi = weightKg / (heightM * heightM);
      setBmiResult(parseFloat(bmi.toFixed(1)));
    }
  };

  const calculateHOMA = () => {
    const glucose = parseFloat(fastingGlucose);
    const insulin = parseFloat(fastingInsulin);
    if (glucose > 0 && insulin > 0) {
      const homa = (glucose * insulin) / 405;
      setHomaResult(parseFloat(homa.toFixed(2)));
    }
  };

  const calculateEGFR = () => {
    const creat = parseFloat(creatinine);
    const ageNum = parseFloat(age);
    if (creat > 0 && ageNum > 0) {
      const genderFactor = gender === 'female' ? 0.85 : 1;
      const egfr = ((140 - ageNum) * 72 * genderFactor) / creat;
      setEgfrResult(parseFloat(egfr.toFixed(1)));
    }
  };

  const getBMICategory = (bmi: number) => {
    if (bmi < 18.5) return { text: 'Underweight', color: 'text-blue-600' };
    if (bmi < 25) return { text: 'Normal', color: 'text-green-600' };
    if (bmi < 30) return { text: 'Overweight', color: 'text-yellow-600' };
    return { text: 'Obese', color: 'text-red-600' };
  };

  const getHOMACategory = (homa: number) => {
    if (homa < 1) return { text: 'Optimal', color: 'text-green-600' };
    if (homa < 2) return { text: 'Normal', color: 'text-green-600' };
    if (homa < 2.9) return { text: 'Early Insulin Resistance', color: 'text-yellow-600' };
    return { text: 'Significant Insulin Resistance', color: 'text-red-600' };
  };

  const getEGFRCategory = (egfr: number) => {
    if (egfr >= 90) return { text: 'Normal', color: 'text-green-600' };
    if (egfr >= 60) return { text: 'Mild Reduction', color: 'text-yellow-600' };
    if (egfr >= 30) return { text: 'Moderate Reduction', color: 'text-orange-600' };
    return { text: 'Severe Reduction', color: 'text-red-600' };
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-dropsugar-green/10 rounded-full filter blur-3xl opacity-60" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-dropsugar-green/20 rounded-full filter blur-3xl opacity-40" />
      </div>

      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center group cursor-pointer">
              <img
                src="/dropsugar-logo.jpg"
                alt="DropSugar"
                className="h-6 sm:h-8 md:h-9 lg:h-10 w-auto object-contain"
              />
            </div>

            <div className="hidden lg:flex items-center space-x-2">
              {onViewPackages && (
                <button
                  onClick={onViewPackages}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10"
                >
                  Plans
                </button>
              )}
              <button
                onClick={() => scrollToSection('dashboard')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeSection === 'dashboard'
                    ? 'bg-dropsugar-green text-white shadow-md'
                    : 'text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeSection === 'how-it-works'
                    ? 'bg-dropsugar-green text-white shadow-md'
                    : 'text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10'
                }`}
              >
                How it works
              </button>
              <button
                onClick={() => scrollToSection('why-glucose-monitoring')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeSection === 'why-glucose-monitoring'
                    ? 'bg-dropsugar-green text-white shadow-md'
                    : 'text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10'
                }`}
              >
                Why glucose monitoring
              </button>
              <button
                onClick={() => scrollToSection('calculators')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeSection === 'calculators'
                    ? 'bg-dropsugar-green text-white shadow-md'
                    : 'text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10'
                }`}
              >
                Calculators
              </button>
              <button
                onClick={() => scrollToSection('contact-us')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeSection === 'contact-us'
                    ? 'bg-dropsugar-green text-white shadow-md'
                    : 'text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10'
                }`}
              >
                Contact Us
              </button>
                  <button
                onClick={onSignIn}
                className="px-6 py-2 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-lg text-sm font-medium transition-all shadow-md hover:shadow-lg"
              >
                Sign In
              </button>
            </div>

            <div className="lg:hidden flex items-center space-x-2">
              <button
                onClick={onSignIn}
                className="px-4 py-2 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-lg text-xs sm:text-sm font-medium transition-all shadow-md"
              >
                Sign In
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-700 hover:bg-dropsugar-green/10 rounded-lg transition-all"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2 border-t border-gray-200 pt-4">
              {onViewPackages && (
                <button
                  onClick={() => {
                    onViewPackages();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all text-gray-700 hover:bg-dropsugar-green/10"
                >
                  Plans
                </button>
              )}
              <button
                onClick={() => {
                  scrollToSection('dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeSection === 'dashboard'
                    ? 'bg-dropsugar-green text-white'
                    : 'text-gray-700 hover:bg-dropsugar-green/10'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => {
                  scrollToSection('video');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeSection === 'video'
                    ? 'bg-dropsugar-green text-white'
                    : 'text-gray-700 hover:bg-dropsugar-green/10'
                }`}
              >
                Video
              </button>
              <button
                onClick={() => {
                  scrollToSection('how-it-works');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeSection === 'how-it-works'
                    ? 'bg-dropsugar-green text-white'
                    : 'text-gray-700 hover:bg-dropsugar-green/10'
                }`}
              >
                How it works
              </button>
              <button
                onClick={() => {
                  scrollToSection('why-glucose-monitoring');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeSection === 'why-glucose-monitoring'
                    ? 'bg-dropsugar-green text-white'
                    : 'text-gray-700 hover:bg-dropsugar-green/10'
                }`}
              >
                Why glucose monitoring
              </button>
              <button
                onClick={() => {
                  scrollToSection('calculators');
                  setIsCalculatorsExpanded(true);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeSection === 'calculators'
                    ? 'bg-dropsugar-green text-white'
                    : 'text-gray-700 hover:bg-dropsugar-green/10'
                }`}
              >
                Calculators
              </button>
              <button
                onClick={() => {
                  scrollToSection('contact-us');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeSection === 'contact-us'
                    ? 'bg-dropsugar-green text-white'
                    : 'text-gray-700 hover:bg-dropsugar-green/10'
                }`}
              >
                Contact Us
              </button>
            </div>
          )}
        </div>
      </nav>

      <div className="pt-24 sm:pt-32 md:pt-36 lg:pt-40 relative z-10">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-24 text-center">
          <div className="mb-6 sm:mb-8 inline-block">
            <div className="flex items-center space-x-2 px-4 sm:px-5 py-2 rounded-full bg-dropsugar-green/10 border border-dropsugar-green/30">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-dropsugar-green" />
              <span className="text-xs sm:text-sm font-medium text-dropsugar-blue">Join thousands transforming their health</span>
            </div>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight text-gray-900 px-4">
            Unleash your healthiest potential
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-4">
            with realtime insights and guidance from true specialists
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <button
              onClick={onSignIn}
              className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Your 14-Day Journey
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
            {onViewPackages && (
              <button
                onClick={onViewPackages}
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white hover:bg-dropsugar-green/10 text-dropsugar-green font-semibold text-base sm:text-lg rounded-full border-2 border-dropsugar-green transition-all duration-300"
              >
                View Plans
              </button>
            )}
          </div>
        </section>

        <section id="dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="mb-8 sm:mb-12 text-center">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 text-gray-900">Dashboard</h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              DropSugar helps you understand how your own body reacts to food, with guidance from doctors. By identifying what actually raises your sugar, we help you make the right changes and lower it naturally, <span className="font-semibold text-gray-800">"because your sugar response is personal."</span>
            </p>
          </div>

          <div id="video" className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden mb-8 sm:mb-12 group shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <button className="group/btn relative">
                <div className="absolute inset-0 bg-dropsugar-green rounded-full blur-xl opacity-50 group-hover/btn:opacity-70 transition-all" />
                <div className="relative bg-white/20 backdrop-blur-md p-4 sm:p-6 md:p-8 rounded-full hover:bg-white/30 transition-all hover:scale-110 shadow-xl">
                  <Play className="w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 text-white" fill="white" />
                </div>
              </button>
            </div>
            <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-4 sm:left-6 md:left-8 z-10 text-white">
              <h4 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">Your Transformation Journey</h4>
              <p className="text-white/90 text-sm sm:text-base md:text-lg">Watch how DropSugar changes lives in just 3 months</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {[
              { icon: Activity, title: 'Diabetic Package', desc: 'Comprehensive glucose monitoring and personalized diabetes management program', color: 'blue' },
              { icon: TrendingDown, title: 'Obesity Program', desc: 'Target 10kg weight loss through glucose-guided nutrition and lifestyle changes', color: 'blue' },
              { icon: Heart, title: 'Hypertension', desc: 'Blood pressure management through metabolic health optimization', color: 'blue' },
              { icon: Shield, title: 'Fatty Liver', desc: 'Reverse fatty liver disease with targeted glucose management strategies', color: 'blue' },
              { icon: Target, title: 'PCOD', desc: 'Hormonal balance and metabolic health through glucose management and lifestyle modification', color: 'blue' }
            ].map((program, idx) => {
              const Icon = program.icon;
              return (
                <div key={idx} className={`group bg-white border-2 border-gray-200 hover:border-dropsugar-green rounded-xl sm:rounded-2xl p-6 sm:p-8 cursor-pointer transition-all duration-300 hover:shadow-xl ${idx === 4 ? 'sm:col-span-2 sm:max-w-md sm:mx-auto' : ''}`}>
                  <div className="mb-4 sm:mb-5 p-3 sm:p-4 bg-dropsugar-green/10 rounded-xl w-fit group-hover:bg-dropsugar-green/20 transition-colors">
                    <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-dropsugar-green" />
                  </div>
                  <h4 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-gray-900">{program.title}</h4>
                  <p className="text-gray-600 mb-4 sm:mb-5 text-sm leading-relaxed">{program.desc}</p>
                  <button
                    onClick={() => {
                      if (program.title === 'Diabetic Package') setIsDiabetesModalOpen(true);
                      if (program.title === 'Obesity Program') setIsObesityModalOpen(true);
                      if (program.title === 'Fatty Liver') setIsFattyLiverModalOpen(true);
                      if (program.title === 'Hypertension') setIsHypertensionModalOpen(true);
                      if (program.title === 'PCOD') setIsPCODModalOpen(true);
                    }}
                    className="text-dropsugar-green font-semibold flex items-center space-x-2 group-hover:text-dropsugar-blue hover:translate-x-1 transition-all"
                  >
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
            <div className="lg:flex-[3] bg-dropsugar-green/10 border-2 border-blue-100 rounded-xl sm:rounded-2xl p-6 sm:p-8">
              <h4 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 flex items-center space-x-3 text-gray-900">
                <div className="p-3 bg-dropsugar-green rounded-xl shadow-md">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <span>The Helping Hand</span>
              </h4>
              <div className="space-y-4">
                {[
                  { icon: Users, title: 'Expert Specialists', desc: 'Connect with endocrinologists, nutritionists, and diabetes educators who understand your unique journey' },
                  { icon: BarChart3, title: 'Personalized Insights', desc: 'AI-powered analysis of your glucose patterns with actionable recommendations tailored to your lifestyle' },
                  { icon: Clock, title: '24/7 Monitoring', desc: 'Continuous glucose tracking with real-time alerts and insights to keep you on track every moment' }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-dropsugar-green hover:shadow-md transition-all">
                      <div className="flex items-start space-x-4">
                        <div className="p-3 bg-dropsugar-green/10 rounded-lg group-hover:bg-dropsugar-green/20 transition-colors">
                          <Icon className="w-5 h-5 text-dropsugar-green" />
                        </div>
                        <div>
                          <h5 className="font-bold mb-2 text-gray-900">{item.title}</h5>
                          <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:flex-1 bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl flex flex-col overflow-hidden shadow-lg min-h-[400px] lg:min-h-0">
              <div className="bg-dropsugar-green text-white p-4 sm:p-5 border-b border-dropsugar-blue-dark">
                <div className="flex items-center space-x-2">
                  <MessageCircle className="w-5 h-5" />
                  <h4 className="font-bold">AI Assistant</h4>
                </div>
                <p className="text-xs text-blue-100 mt-2">CME • Diabetic Support</p>
              </div>

              <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-gray-50">
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.isUser ? 'bg-dropsugar-green text-white shadow-md' : 'bg-white text-gray-800 border border-gray-200 shadow-sm'}`}>
                      <p className="text-sm">{msg.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Ask me anything..."
                    className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-dropsugar-green text-sm text-gray-900 placeholder-gray-500"
                  />
                  <button onClick={handleSendMessage} className="bg-dropsugar-green hover:bg-dropsugar-green-dark text-white p-2 rounded-full shadow-md hover:shadow-lg transition-all">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 text-gray-900">How DropSugar Works</h3>
            <p className="text-base sm:text-lg text-gray-600">Your journey to better health, step by step</p>
          </div>

          <div className="relative">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-0.5 sm:w-1 bg-gradient-to-b from-dropsugar-blue via-dropsugar-green to-dropsugar-blue-dark sm:transform sm:-translate-x-1/2" />

            <div className="space-y-6 sm:space-y-8">
              {[
                {
                  step: '1',
                  title: 'Onboarding',
                  description: 'You enroll in DropSugar and meet our medical team with baseline blood tests'
                },
                {
                  step: '2',
                  title: 'Glucose Sensor Applied',
                  description: 'A small continuous glucose sensor (CGM) is placed to track your sugar levels day and night.'
                },
                {
                  step: '3',
                  title: 'Eat Normally (Baseline Phase)',
                  description: 'For the first three days, you eat your regular food so we understand your body\'s natural sugar patterns.'
                },
                {
                  step: '4',
                  title: 'Meal Logging',
                  description: 'You simply take photos of what you eat and log your meals.'
                },
                {
                  step: '5',
                  title: 'Sugar Response Analysis',
                  description: 'After three days, our specialists analyze your glucose data to identify exactly which foods and habits spike your sugar.'
                },
                {
                  step: '6',
                  title: 'Personalized Plan',
                  description: 'You receive a diet, activity, and lifestyle plan tailored to your sugar response.'
                },
                {
                  step: '7',
                  title: 'Guided Changes',
                  description: 'With continuous monitoring and specialist support, you make small, practical changes.'
                },
                {
                  step: '8',
                  title: 'Sugar Drops, Health Improves',
                  description: 'Blood sugar levels improve naturally, insulin sensitivity increases, and medications can often be reduced under medical supervision.'
                }
              ].map((item, idx) => (
                <div key={item.step} className={`flex items-center gap-4 sm:gap-8 ${idx % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'} flex-row`}>
                  <div className={`flex-1 ${idx % 2 === 0 ? 'sm:text-right' : 'sm:text-left'} text-left pl-12 sm:pl-0`}>
                    <div className={`inline-block bg-white border-2 border-dropsugar-green/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:border-dropsugar-green w-full ${idx % 2 === 0 ? 'sm:text-right' : 'sm:text-left'} text-left`}>
                      <div className={`flex items-start gap-3 sm:gap-4 ${idx % 2 === 0 ? 'sm:flex-row-reverse' : 'sm:flex-row'} flex-row`}>
                        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark text-white rounded-full flex items-center justify-center text-lg sm:text-xl font-bold shadow-md">
                          {item.step}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2 text-gray-900">{item.title}</h4>
                          <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute left-4 sm:left-1/2 sm:relative z-10 flex-shrink-0 sm:transform sm:-translate-x-0">
                    <div className="w-4 h-4 sm:w-6 sm:h-6 bg-dropsugar-green rounded-full border-2 sm:border-4 border-white shadow-lg" />
                  </div>

                  <div className="hidden sm:block flex-1" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="why-glucose-monitoring" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20">
          <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark" />
            <div className="absolute inset-0 opacity-30 bg-gradient-to-t from-black/40 to-transparent" />
            <div className="relative z-10 p-6 sm:p-10 md:p-16 text-white">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">Why Glucose Monitoring Matters</h3>

              <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 mb-6 sm:mb-8">
                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-white/95">
                  Blood sugar does not rise the same way in everyone. The same food, stress, sleep, or activity can cause very different sugar spikes in different people. Without continuous glucose monitoring, these hidden spikes go unnoticed, leading to poor control, medication dependence, and long-term complications.
                </p>
              </div>

              <div className="h-1 w-16 sm:w-24 bg-white/50 rounded mx-auto my-6 sm:my-8" />

              <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8">
                <h4 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 flex items-center space-x-2 sm:space-x-3">
                  <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8" />
                  <span>The DropSugar Solution</span>
                </h4>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-white/95">
                  DropSugar uses continuous glucose monitoring to show how your own body responds to food and daily habits in real time. Our specialists analyze this data and guide you with personalized, practical changes that lower sugar naturally—<span className="font-semibold text-white">because your sugar response is personal.</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="calculators" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden">
            <button
              onClick={() => setIsCalculatorsExpanded(!isCalculatorsExpanded)}
              className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <img src="/dropsugar-logo.jpg" alt="DropSugar" className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Calculators</h3>
              </div>
              <ChevronDown className={`w-6 h-6 text-gray-600 transition-transform duration-300 ${isCalculatorsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {isCalculatorsExpanded && (
              <div className="px-6 pb-6">
                <div className="flex justify-center flex-wrap gap-4 mb-6">
                  <button
                    onClick={() => setActiveCalculator('bmi')}
                    className={`px-6 py-2 rounded-lg font-medium transition-all ${
                      activeCalculator === 'bmi'
                        ? 'bg-dropsugar-blue text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    BMI
                  </button>
                  <button
                    onClick={() => setActiveCalculator('homa')}
                    className={`px-6 py-2 rounded-lg font-medium transition-all ${
                      activeCalculator === 'homa'
                        ? 'bg-dropsugar-blue text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    HOMA-IR
                  </button>
                  <button
                    onClick={() => setActiveCalculator('egfr')}
                    className={`px-6 py-2 rounded-lg font-medium transition-all ${
                      activeCalculator === 'egfr'
                        ? 'bg-dropsugar-blue text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    eGFR
                  </button>
                </div>

                {activeCalculator === 'bmi' && (
                  <div className="max-w-md mx-auto bg-gradient-to-br from-dropsugar-blue/5 to-dropsugar-green/5 rounded-xl p-6 border border-gray-200">
                    <h4 className="text-2xl font-bold mb-4 text-gray-900">BMI Calculator</h4>
                    <p className="text-sm text-gray-600 mb-6">Body Mass Index helps assess if your weight is in a healthy range</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                        <input
                          type="number"
                          value={bmiHeight}
                          onChange={(e) => setBmiHeight(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                          placeholder="170"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
                        <input
                          type="number"
                          value={bmiWeight}
                          onChange={(e) => setBmiWeight(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                          placeholder="70"
                        />
                      </div>
                      <button
                        onClick={calculateBMI}
                        className="w-full py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-lg transition-all shadow-md"
                      >
                        Calculate BMI
                      </button>
                      {bmiResult !== null && (
                        <>
                          <div className="mt-4 p-4 bg-white rounded-lg border-2 border-dropsugar-green/30">
                            <p className="text-lg font-semibold text-gray-900">Your BMI: {bmiResult}</p>
                            <p className={`text-sm font-medium mt-1 ${getBMICategory(bmiResult).color}`}>
                              {getBMICategory(bmiResult).text}
                            </p>
                          </div>
                          {bmiResult >= 30 && (
                            <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border-2 border-red-300 shadow-md">
                              <div className="flex items-start gap-4">
                                <div className="relative">
                                  <User className="w-10 h-10 text-red-600" />
                                  <AlertTriangle className="w-5 h-5 text-red-600 absolute -top-1 -right-1" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-red-800 font-bold text-lg mb-2">Health Alert!</p>
                                  <p className="text-red-700 text-sm leading-relaxed mb-3">
                                    Your BMI indicates obesity, which may increase health risks. Take control of your health today.
                                  </p>
                                  <button
                                    onClick={onSignIn}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all shadow-md"
                                  >
                                    <Sparkles className="w-4 h-4" />
                                    Try DropSugar and live the life
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {activeCalculator === 'homa' && (
                  <div className="max-w-md mx-auto bg-gradient-to-br from-dropsugar-blue/5 to-dropsugar-green/5 rounded-xl p-6 border border-gray-200">
                    <h4 className="text-2xl font-bold mb-4 text-gray-900">HOMA-IR Calculator</h4>
                    <p className="text-sm text-gray-600 mb-6">Homeostatic Model Assessment of Insulin Resistance</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fasting Glucose (mg/dL)</label>
                        <input
                          type="number"
                          value={fastingGlucose}
                          onChange={(e) => setFastingGlucose(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                          placeholder="90"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fasting Insulin (μU/mL)</label>
                        <input
                          type="number"
                          value={fastingInsulin}
                          onChange={(e) => setFastingInsulin(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                          placeholder="5"
                        />
                      </div>
                      <button
                        onClick={calculateHOMA}
                        className="w-full py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-lg transition-all shadow-md"
                      >
                        Calculate HOMA-IR
                      </button>
                      {homaResult !== null && (
                        <>
                          <div className="mt-4 p-4 bg-white rounded-lg border-2 border-dropsugar-green/30">
                            <p className="text-lg font-semibold text-gray-900">Your HOMA-IR: {homaResult}</p>
                            <p className={`text-sm font-medium mt-1 ${getHOMACategory(homaResult).color}`}>
                              {getHOMACategory(homaResult).text}
                            </p>
                          </div>
                          {homaResult >= 2.9 && (
                            <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border-2 border-red-300 shadow-md">
                              <div className="flex items-start gap-4">
                                <div className="relative">
                                  <User className="w-10 h-10 text-red-600" />
                                  <AlertTriangle className="w-5 h-5 text-red-600 absolute -top-1 -right-1" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-red-800 font-bold text-lg mb-2">Health Alert!</p>
                                  <p className="text-red-700 text-sm leading-relaxed mb-3">
                                    Significant insulin resistance detected. This requires attention to prevent diabetes and metabolic complications.
                                  </p>
                                  <button
                                    onClick={onSignIn}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all shadow-md"
                                  >
                                    <Sparkles className="w-4 h-4" />
                                    Try DropSugar and live the life
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {activeCalculator === 'egfr' && (
                  <div className="max-w-md mx-auto bg-gradient-to-br from-dropsugar-blue/5 to-dropsugar-green/5 rounded-xl p-6 border border-gray-200">
                    <h4 className="text-2xl font-bold mb-4 text-gray-900">eGFR Calculator</h4>
                    <p className="text-sm text-gray-600 mb-6">Estimated Glomerular Filtration Rate for kidney function</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Creatinine (mg/dL)</label>
                        <input
                          type="number"
                          value={creatinine}
                          onChange={(e) => setCreatinine(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                          placeholder="1.0"
                          step="0.1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Age (years)</label>
                        <input
                          type="number"
                          value={age}
                          onChange={(e) => setAge(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                          placeholder="45"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                        <div className="flex space-x-4">
                          <button
                            onClick={() => setGender('male')}
                            className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                              gender === 'male'
                                ? 'bg-dropsugar-blue text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Male
                          </button>
                          <button
                            onClick={() => setGender('female')}
                            className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                              gender === 'female'
                                ? 'bg-dropsugar-blue text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Female
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={calculateEGFR}
                        className="w-full py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-lg transition-all shadow-md"
                      >
                        Calculate eGFR
                      </button>
                      {egfrResult !== null && (
                        <>
                          <div className="mt-4 p-4 bg-white rounded-lg border-2 border-dropsugar-green/30">
                            <p className="text-lg font-semibold text-gray-900">Your eGFR: {egfrResult} mL/min/1.73m²</p>
                            <p className={`text-sm font-medium mt-1 ${getEGFRCategory(egfrResult).color}`}>
                              {getEGFRCategory(egfrResult).text}
                            </p>
                          </div>
                          {egfrResult < 30 && (
                            <div className="mt-4 p-5 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border-2 border-red-300 shadow-md">
                              <div className="flex items-start gap-4">
                                <div className="relative">
                                  <User className="w-10 h-10 text-red-600" />
                                  <AlertTriangle className="w-5 h-5 text-red-600 absolute -top-1 -right-1" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-red-800 font-bold text-lg mb-2">Health Alert!</p>
                                  <p className="text-red-700 text-sm leading-relaxed mb-3">
                                    Severe kidney function reduction detected. Immediate medical attention and lifestyle management are crucial.
                                  </p>
                                  <button
                                    onClick={onSignIn}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all shadow-md"
                                  >
                                    <Sparkles className="w-4 h-4" />
                                    Try DropSugar and live the life
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section id="contact-us" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20 bg-gradient-to-br from-blue-50 to-white">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 text-gray-900">Meet Our Team</h3>
            <p className="text-base sm:text-lg text-gray-600">Expert healthcare professionals dedicated to your wellness journey</p>
          </div>

          <div className="space-y-8 sm:space-y-12">
            <div className="bg-white border-2 border-dropsugar-green/30 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-xl">
              <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-8">
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  <div className="w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl flex items-center justify-center text-white text-4xl sm:text-5xl font-bold shadow-lg">
                    DA
                  </div>
                </div>
                <div className="flex-1">
                  <div className="mb-4">
                    <div className="inline-block px-4 py-1 bg-dropsugar-green/20 text-dropsugar-blue rounded-full text-xs sm:text-sm font-semibold mb-3">
                      Our Visionary - "The Sugar Doctor"
                    </div>
                    <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Dr. Abdussamad NM</h4>
                    <p className="text-dropsugar-green font-semibold text-base sm:text-lg mb-4">Senior Consultant Diabetologist</p>
                  </div>
                  <div className="text-gray-700 text-sm sm:text-base leading-relaxed space-y-3">
                    <p>
                      Dr. Abdussamad NM is a Senior Consultant Diabetologist with over 25 years of clinical experience, widely recognized for delivering robust, evidence-based diabetes care through deep clinical expertise and advanced technical proficiency. He completed his MBBS from Calicut Medical College, followed by an MD in General (Internal) Medicine from Manipal Academy of Higher Education (MAHE), Manipal University, and further enhanced his global credentials with a Post-Graduate Diploma in Diabetes from the University of Leicester, UK.
                    </p>
                    <p>
                      His distinguished career spans both India and the Middle East, having served as a Senior Lecturer in the Department of Medicine at Calicut Medical College, Registrar at Al Amiri Hospital, Kuwait, Consultant Physician and Diabetologist at MIMS Hospital, Kottakal, and Consultant Physician at National Hospital, Jeddah.
                    </p>
                    <p>
                      Now in leadership roles as medical director in Asten Ortho and Superident in Metromed international Cardiac centre.
                    </p>
                    <p>
                      Renowned for his expertise in General Medicine and Diabetology, Dr. Abdussamad NM is regarded as one of the region's most respected specialists, with extensive experience in managing all forms of diabetes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {[
                {
                  initials: 'TM1',
                  name: 'Team Member 1',
                  title: 'Healthcare Specialist',
                  bio: 'Dedicated healthcare professional with extensive experience in patient care and wellness programs. Committed to helping patients achieve their health goals through personalized treatment plans.',
                  color: 'from-teal-500 to-teal-700'
                },
                {
                  initials: 'TM2',
                  name: 'Team Member 2',
                  title: 'Medical Consultant',
                  bio: 'Experienced medical consultant specializing in chronic disease management and preventive care. Passionate about using technology to improve patient outcomes and quality of life.',
                  color: 'from-green-500 to-green-700'
                },
                {
                  initials: 'TM3',
                  name: 'Team Member 3',
                  title: 'Clinical Specialist',
                  bio: 'Expert clinical specialist with a focus on metabolic health and diabetes management. Dedicated to providing evidence-based care and supporting patients on their wellness journey.',
                  color: 'from-orange-500 to-orange-700'
                },
                {
                  initials: 'TM4',
                  name: 'Team Member 4',
                  title: 'Health Coach',
                  bio: 'Certified health coach specializing in lifestyle modification and behavioral change. Works closely with patients to develop sustainable habits and achieve long-term health improvements.',
                  color: 'from-pink-500 to-pink-700'
                }
              ].map((member, idx) => (
                <div key={idx} className="bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all hover:border-blue-300">
                  <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className={`flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br ${member.color} rounded-xl flex items-center justify-center text-white text-xl sm:text-2xl font-bold shadow-md mx-auto sm:mx-0`}>
                      {member.initials}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h5 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{member.name}</h5>
                      <p className="text-dropsugar-green font-semibold text-sm mb-3">{member.title}</p>
                      <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="get-started" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20">
          <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 md:p-16 text-center shadow-xl">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-gray-900">Ready to Transform Your Life?</h3>
            <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-12 max-w-2xl mx-auto px-4">
              Join thousands who have already taken control of their metabolic health
            </p>
            <button
              onClick={onSignIn}
              className="group inline-flex items-center justify-center px-8 sm:px-10 py-3 sm:py-4 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Your 14-Day Program
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-xs sm:text-sm text-gray-600 mt-6 sm:mt-8 px-4">
              Includes GlucoRx CGM device • Expert consultations • AI-powered insights
            </p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20">
          <InstagramReelsWidget />
        </section>

        <footer className="border-t border-gray-200 bg-gray-50 py-8 sm:py-12 mt-12 sm:mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center">
                <img
                  src="/dropsugar-logo.jpg"
                  alt="DropSugar"
                  className="h-6 sm:h-7 md:h-8 w-auto object-contain"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
                <a href="/delete-account" className="hover:text-gray-800 underline-offset-2 hover:underline">
                  Delete account
                </a>
                <a href="mailto:support@dropsugar.io" className="hover:text-gray-800 underline-offset-2 hover:underline">
                  Support
                </a>
                <p>© 2025 DropSugar. All rights reserved.</p>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {isDiabetesModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative animate-slideUp">
            <div className="sticky top-0 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark text-white px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 rounded-t-2xl sm:rounded-t-3xl flex items-center justify-between">
              <div className="flex-1 pr-4">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">Understanding Type 2 Diabetes</h3>
                <p className="text-blue-100 text-xs sm:text-sm">How DropSugar helps you take control</p>
              </div>
              <button
                onClick={() => setIsDiabetesModalOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">The Real Issue</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Type 2 diabetes isn't just about high blood sugar—it's about how your body responds to insulin.
                      Many people experience blood sugar spikes after meals due to their food choices and lifestyle,
                      which can make the condition worse over time, even with medication.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">How DropSugar Works</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      We use continuous glucose monitoring (CGM) to track your blood sugar in real-time. This shows us exactly
                      how your body responds to different foods, activities, sleep patterns, and daily routines—giving you and
                      your healthcare team a complete picture of what's happening.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      With this data, we can pinpoint the specific triggers causing your blood sugar to spike and identify
                      patterns that affect your metabolic health.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Your Personalized Plan</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Based on your unique data, we create a medically supervised plan tailored just for you. This includes:
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-dropsugar-green flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Smart nutrition choices</strong> that work with your lifestyle and preferences</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-dropsugar-green flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Structured physical activity</strong> designed for your fitness level</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-dropsugar-green flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Sustainable lifestyle adjustments</strong> you can maintain long-term</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Real Results</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      By addressing the root causes of your blood sugar issues, DropSugar helps you achieve:
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-dropsugar-green flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Better blood sugar control with fewer spikes</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-dropsugar-green flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">More stable energy levels throughout the day</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-dropsugar-green flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Potential to reduce or optimize your medications (when appropriate with your doctor)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-6 text-white">
                <div className="text-center">
                  <Heart className="w-12 h-12 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold mb-3">Your Journey is Personal</h4>
                  <p className="text-blue-100 text-lg leading-relaxed">
                    Everyone's body responds differently to food, stress, and activity. That's why DropSugar focuses on
                    YOUR unique patterns—because your sugar response is personal, and your care should be too.
                  </p>
                </div>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={() => setIsDiabetesModalOpen(false)}
                  className="px-8 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  Got It, Thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isObesityModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative animate-slideUp">
            <div className="sticky top-0 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark text-white px-8 py-6 rounded-t-3xl flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-2">Understanding Obesity</h3>
                <p className="text-blue-100 text-sm">How DropSugar addresses the root cause</p>
              </div>
              <button
                onClick={() => setIsObesityModalOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">The Real Issue</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Obesity is not simply a result of excess calorie intake; it is closely linked to insulin resistance and metabolic dysregulation. When insulin levels remain chronically elevated, the body is driven toward fat storage, impaired fat oxidation, increased hunger, and reduced metabolic flexibility, making sustained weight loss difficult despite dieting and exercise.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">How DropSugar Works</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      DropSugar uses continuous glucose monitoring (CGM) to evaluate individual glycaemic responses to food, physical activity, sleep, and stress. This allows clinicians to identify dietary patterns and lifestyle factors that perpetuate insulin resistance and promote weight gain, many of which are not apparent through conventional assessment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Your Personalized Plan</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Based on these insights, a personalized, medically supervised intervention is implemented to improve insulin sensitivity through targeted nutritional strategies, structured physical activity, and sustainable behavioral modification. By correcting the underlying metabolic dysfunction rather than focusing solely on calorie restriction, DropSugar supports healthy fat loss, improved metabolic health, and long-term weight management—because metabolic responses are personal.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-6 text-white">
                <div className="text-center">
                  <Heart className="w-12 h-12 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold mb-3">Your Journey is Personal</h4>
                  <p className="text-blue-100 text-lg leading-relaxed">
                    Everyone's body responds differently to food, stress, and activity. That's why DropSugar focuses on
                    YOUR unique patterns—because your metabolic response is personal, and your care should be too.
                  </p>
                </div>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={() => setIsObesityModalOpen(false)}
                  className="px-8 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  Got It, Thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isFattyLiverModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative animate-slideUp">
            <div className="sticky top-0 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark text-white px-8 py-6 rounded-t-3xl flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-2">Understanding Fatty Liver Disease</h3>
                <p className="text-blue-100 text-sm">How DropSugar helps reverse fatty liver</p>
              </div>
              <button
                onClick={() => setIsFattyLiverModalOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">The Real Issue</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Non-alcoholic fatty liver disease (NAFLD) is strongly associated with insulin resistance and metabolic dysfunction. Excess glucose and insulin in the bloodstream promote fat accumulation in the liver, leading to inflammation, impaired liver function, and long-term risks such as type 2 diabetes, cardiovascular disease, and progressive liver damage.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">How DropSugar Works</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      DropSugar uses continuous glucose monitoring (CGM) to track your body's real-time glucose response to meals, activity, sleep, and stress. This allows clinicians to identify dietary and lifestyle patterns that drive insulin resistance and hepatic fat accumulation, which are often missed in routine assessments.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Your Personalized Plan</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Based on these insights, a personalized, medically supervised intervention is designed, focusing on improving insulin sensitivity through targeted nutrition, structured physical activity, and sustainable lifestyle modifications. By addressing the metabolic root cause rather than just the symptom of fat accumulation, DropSugar supports liver health, improved metabolic function, and long-term disease prevention—because each patient's metabolic response is unique.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-6 text-white">
                <div className="text-center">
                  <Heart className="w-12 h-12 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold mb-3">Your Journey is Personal</h4>
                  <p className="text-blue-100 text-lg leading-relaxed">
                    Everyone's body responds differently to food, stress, and activity. That's why DropSugar focuses on
                    YOUR unique patterns—because your metabolic response is personal, and your care should be too.
                  </p>
                </div>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={() => setIsFattyLiverModalOpen(false)}
                  className="px-8 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  Got It, Thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isHypertensionModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative animate-slideUp">
            <div className="sticky top-0 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark text-white px-8 py-6 rounded-t-3xl flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-2">Understanding Hypertension</h3>
                <p className="text-blue-100 text-sm">How DropSugar addresses metabolic blood pressure control</p>
              </div>
              <button
                onClick={() => setIsHypertensionModalOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">The Real Issue</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Hypertension is frequently part of a broader metabolic disorder and is strongly associated with insulin resistance, hyperinsulinaemia, and endothelial dysfunction. Elevated insulin levels contribute to increased sympathetic nervous system activity, renal sodium retention, vascular stiffness, and low-grade inflammation—all of which play a significant role in the development and persistence of high blood pressure.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">How DropSugar Works</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      DropSugar utilizes continuous glucose monitoring (CGM) to assess individual glucose patterns in response to meals, activity, sleep, and daily stressors. These patterns often reveal underlying metabolic triggers that worsen insulin resistance and indirectly drive blood pressure elevation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Your Personalized Plan</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Using this data, our specialists design a personalized, medically supervised lifestyle intervention aimed at improving insulin sensitivity, restoring metabolic balance, and reducing cardiovascular risk. By addressing the metabolic root cause rather than treating blood pressure in isolation, DropSugar supports better blood pressure control, improved vascular health, and rational optimization of antihypertensive therapy where clinically appropriate—because metabolic responses vary from person to person.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-6 text-white">
                <div className="text-center">
                  <Heart className="w-12 h-12 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold mb-3">Your Journey is Personal</h4>
                  <p className="text-blue-100 text-lg leading-relaxed">
                    Everyone's body responds differently to food, stress, and activity. That's why DropSugar focuses on
                    YOUR unique patterns—because your metabolic response is personal, and your care should be too.
                  </p>
                </div>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={() => setIsHypertensionModalOpen(false)}
                  className="px-8 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  Got It, Thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isPCODModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative animate-slideUp">
            <div className="sticky top-0 bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark text-white px-8 py-6 rounded-t-3xl flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-2">Understanding PCOD</h3>
                <p className="text-blue-100 text-sm">How DropSugar supports hormonal balance and metabolic health</p>
              </div>
              <button
                onClick={() => setIsPCODModalOpen(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">The Real Issue</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Polycystic Ovarian Disease (PCOD) is closely linked to insulin resistance and metabolic dysregulation, which affect hormonal balance, ovulatory function, and weight regulation. Chronically elevated insulin levels drive androgen excess, disrupt menstrual cycles, and contribute to long-term risks such as type 2 diabetes and cardiovascular disease.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">How DropSugar Works</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      DropSugar uses continuous glucose monitoring (CGM) to evaluate individual glycaemic responses to meals, physical activity, sleep, and stress. This allows clinicians to identify metabolic patterns that exacerbate insulin resistance and hormonal imbalance, which are often invisible through conventional assessment.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 border-2 border-dropsugar-green/30">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-dropsugar-green rounded-xl">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">Your Personalized Plan</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Based on this analysis, a personalized, medically supervised intervention is implemented, focusing on targeted nutrition, structured physical activity, and sustainable lifestyle modification to improve insulin sensitivity. By addressing the metabolic root cause, DropSugar supports hormonal balance, regular ovulation, weight management, and long-term metabolic health—because each patient's metabolic response is unique.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-6 text-white">
                <div className="text-center">
                  <Target className="w-12 h-12 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold mb-3">Your Journey is Personal</h4>
                  <p className="text-blue-100 text-lg leading-relaxed">
                    Everyone's body responds differently to food, stress, and activity. That's why DropSugar focuses on
                    YOUR unique patterns—because your metabolic response is personal, and your care should be too.
                  </p>
                </div>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={() => setIsPCODModalOpen(false)}
                  className="px-8 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  Got It, Thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
